import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import { RCP_OPTIONS, RCP_DEFAUT, CULTURES, trouverCultureParLibelle } from '../../shared/constants';
import CourbesHorizonsChart from '../../shared/charts/CourbesHorizonsChart';

// Seules ces familles sont simulées pour le changement de semis ou de variété.
const FAMILLES_ELIGIBLES = ['mais', 'soja'];

/** Adaptations / Changement de date de semis ou de variété (mock). */
export default function ChangementSemisVariete() {
  const { idExploit } = useOAD();
  const [type, setType] = React.useState('semis');
  const [culture, setCulture] = React.useState(null);
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);

  const parametresExploitation = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );
  const {
    data: assolement,
    loading: assolementEnChargement,
    error: assolementErreur,
  } = useApiData(api.getProductionRapport, parametresExploitation);

  // Le filtre maïs/soja ne concerne que le changement de VARIÉTÉ (seules
  // familles simulées pour ça). En date de semis, toutes les cultures de
  // l'assolement sont proposées, comme dans Substitution — pas de mapping
  // vers un identifiant numérique dans ce cas, l'API reçoit le libellé
  // directement (idProduction porte alors une string, pas un id).
  const cultures = React.useMemo(() => {
    if (type === 'variete') {
      // Maïs : TOUTES les variétés (précoce -> tardif), qu'elles figurent ou
      // non dans l'assolement — ce sont elles que l'on compare.
      const varietesMais = CULTURES
        .filter((c) => c.famille === 'mais')
        .map((c) => ({ id: c.id, label: c.label }));
      // Soja : comme avant, d'après l'assolement de l'exploitation.
      const soja = Object.keys(assolement ?? {})
        .map((libelle) => ({ libelle, culture: trouverCultureParLibelle(libelle) }))
        .filter(({ culture: c }) => c && c.famille === 'soja')
        .map(({ libelle, culture: c }) => ({ id: c.id, label: libelle }));
      return [...varietesMais, ...soja];
    }
    if (!assolement) return [];
    return Object.keys(assolement).map((libelle) => ({ id: libelle, label: libelle }));
  }, [assolement, type]);

  // Première culture éligible sélectionnée dès que la liste est connue.
  React.useEffect(() => {
    if (cultures.length > 0 && !cultures.some((c) => c.id === culture)) {
      setCulture(cultures[0].id);
    }
  }, [cultures, culture]);

  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit, type, idProduction: culture, rcp }),
    [idExploit, type, culture, rcp]
  );
  const { data, loading, error } = useApiData(api.getChangementSemisVariete, parametres, {
    skip: !culture,
  });

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="adaptations"
        niveau3="changement-semis-variete"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">Type</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="semis">Semis</option>
            <option value="variete">Variété</option>
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Culture</span>
          <select
            value={culture ?? ''}
            onChange={(e) => setCulture(type === 'variete' ? Number(e.target.value) : e.target.value)}
            disabled={cultures.length === 0}
          >
            {cultures.length === 0 && (
              // Distingue les 3 raisons possibles d'une liste vide : avant, le
              // message unique "Aucune culture simulée" les masquait toutes.
              <option>
                {assolementEnChargement
                  ? 'Chargement…'
                  : assolementErreur
                  ? `Assolement indisponible (${assolementErreur})`
                  : "Aucune culture dans l'assolement"}
              </option>
            )}
            {cultures.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-semis-variete"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">
            {type === 'semis'
              ? 'Effet de la date de semis'
              : 'Effet de la précocité de la variété'}
          </h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading ? (
            <p className="tdb-chargement">Chargement…</p>
          ) : (
            !error && (
              <LimiteErreur cle={`${type}-${culture}-${rcp}`}>
                <CourbesHorizonsChart
                  series={data?.series ?? []}
                  horizons={data?.horizons ?? []}
                />
              </LimiteErreur>
            )
          )}
        </section>
      </main>
    </div>
  );
}
